/**
 * The Daily digest card: what the tenant's daily email is, which layout it uses,
 * and what each one looks like.
 *
 * The Digest switch itself stays with the other master switches on the Events
 * tab, so the banner in the preview dialog is the one reminder here that a
 * preview does not mean anything is going out. It reads the same state that
 * switch shows, the backend's `channel_allowed('digest')`: the email kill switch
 * stops digests too, and every kind on this card goes out through that gate.
 */
import { useRef, useState } from 'react';
import type {
  NotificationDigestPreviewKind,
  NotificationSettingsUpdate,
} from '../../../../lib/api';
import { Alert, AlertDescription } from '../../../ui/alert';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Label } from '../../../ui/label';
import { Switch } from '../../../ui/switch';
import { AlertCircle, Eye, Info, Loader2, Newspaper } from 'lucide-react';
import { useDigestPreview } from './useDigestPreview';
import type { MasterSwitches } from './types';

const PREVIEWS: { kind: NotificationDigestPreviewKind; label: string; shown: string }[] = [
  { kind: 'procurement', label: 'Preview procurement summary', shown: 'Procurement summary' },
  { kind: 'quality', label: 'Preview quality summary', shown: 'Quality summary' },
  { kind: 'daily', label: 'Preview customer digest', shown: 'Daily digest, as a customer gets it' },
  { kind: 'daily_supplier', label: 'Preview supplier digest', shown: 'Daily digest, as a supplier gets it' },
];

interface NotificationDigestCardProps {
  master: MasterSwitches;
  canConfigure: boolean;
  /** CR-8217: the summary style is install-wide, like the master switches. */
  canSetInstallSwitches: boolean;
  saving: boolean;
  /** `meta.one_daily_digest`: the company is on the one daily digest. */
  oneDailyDigest: boolean;
  /** The digest is really going out: on it, with Daily digest, Email and Bell on. */
  digestActive: boolean;
  /** Batched mail is really going out: Digest on, and email not stopped. */
  batchedMail: boolean;
  /** Applies a settings patch immediately and toasts `message` on success. */
  onChange: (patch: NotificationSettingsUpdate, message: string) => void;
}

export function NotificationDigestCard({
  master,
  canConfigure,
  canSetInstallSwitches,
  saving,
  oneDailyDigest,
  digestActive,
  batchedMail,
  onChange,
}: NotificationDigestCardProps) {
  const { preview, loadingKind, error, open, close } = useDigestPreview();
  // No DialogTrigger here, so Radix has nothing to hand focus back to.
  const openedFrom = useRef<HTMLButtonElement | null>(null);
  const [shownKind, setShownKind] = useState<NotificationDigestPreviewKind>('procurement');

  const status = (): string => {
    if (digestActive) {
      return 'Each person gets one email a day at 07:00. Critical and required events, '
        + 'events with their own frequency, and weekly events send as before.';
    }
    if (!oneDailyDigest) {
      return `${master.emailsEnabled ? 'Each event follows its own frequency. ' : ''}`
        + 'Helix sets up the one daily digest for a company.';
    }
    if (!master.bellEnabled) {
      if (!master.emailsEnabled) {
        return 'One daily digest is set up, but the Bell and Email are both off, so these '
          + 'events reach nobody.';
      }
      return master.digestEnabled
        ? 'One daily digest is set up, but the Bell is off, so events follow their own frequency.'
        : 'One daily digest is set up, but the Bell and Daily digest are both off, so only '
          + 'immediate events are emailed.';
    }
    return 'One daily digest is set up, but digest emails are off, so these events reach '
      + 'people by bell only.';
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Newspaper className="h-5 w-5" />
            Daily digest
          </CardTitle>
          <CardDescription>{status()}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* CR-7281: per-install summary style. Applies immediately, like the
              master switches. */}
          <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
            <div className="space-y-1">
              <Label htmlFor="notif-summary-style" className="font-medium">
                Classic Procurement Summary (Supplier Portal layout)
              </Label>
              <p className="text-sm text-muted-foreground">
                {master.effectiveProcurementSummaryStyle === 'classic'
                  ? 'Customer recipients receive the Classic seven-row daily summary, and the '
                    + 'separate OTIF Review email is not sent.'
                    + (master.procurementSummaryStyle ? '' : ' Set by this instance default.')
                  : 'Helix summary in use. Switch on to send customer recipients the Classic '
                    + 'seven-row daily summary instead.'}
              </p>
            </div>
            <Switch
              id="notif-summary-style"
              checked={master.effectiveProcurementSummaryStyle === 'classic'}
              disabled={!canConfigure || saving || !canSetInstallSwitches}
              onCheckedChange={(checked) =>
                onChange(
                  { procurement_summary_style: checked ? 'classic' : 'helix' },
                  checked
                    ? 'Daily summary style set to Classic Procurement Summary'
                    : 'Daily summary style set to Helix summary',
                )
              }
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {PREVIEWS.map(({ kind, label }) => (
              <Button
                key={kind}
                type="button"
                variant="outline"
                size="sm"
                onClick={(event) => {
                  openedFrom.current = event.currentTarget;
                  setShownKind(kind);
                  open(kind);
                }}
                disabled={loadingKind !== null}
              >
                {loadingKind === kind
                  ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <Eye className="mr-1.5 h-3.5 w-3.5" />}
                {label}
              </Button>
            ))}
          </div>
          {error && (
            <p role="alert" className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={preview !== null} onOpenChange={(next) => !next && close()}>
        <DialogContent
          className="max-w-3xl"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            openedFrom.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>{preview?.subject ?? 'Digest preview'}</DialogTitle>
            <DialogDescription className="text-xs">
              {PREVIEWS.find(({ kind }) => kind === shownKind)?.shown}. Rendered with sample
              data and this company&apos;s branding — exactly the layout recipients receive.
            </DialogDescription>
          </DialogHeader>
          {!batchedMail && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Digest emails are off. This is a preview; nothing is being sent.
              </AlertDescription>
            </Alert>
          )}
          {preview && (
            <iframe
              title="Digest preview"
              srcDoc={preview.html}
              sandbox=""
              // Radix focuses the first tabbable element, and inside the frame
              // Escape never reaches the dialog. The cost is mouse-only scrolling:
              // `sandbox=""` keeps the frame cross-origin, so nothing out here can
              // scroll it or read its height to scroll it from a wrapper.
              tabIndex={-1}
              className="w-full h-[65vh] rounded border bg-white"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
